<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="updateButton"
    :secondary-cta="{}"
    @close="closeAction"
    @submit.prevent="onSubmit"
  >
    <div class="row q-col-gutter-xl">
      <div class="col-12">
        <PInput
          v-model.trim="formData.credit_score"
          label="Credit Score"
          placeholder="Enter credit score"
          :validations="{ gte: 350, lte: 900 }"
          required
          type="number"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.delinquencies"
          label="Delinquents"
          placeholder="0"
          :validations="{ gte: 0, lte: 5 }"
          type="number"
          required
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="formData.balance"
          label="Balance"
          placeholder="0"
          type="number"
          :validations="{ gte: 0 }"
          required
          class="prepend-bg"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
      </div>
      <div class="col-6">
        <PField
          v-model="isOverdraftAccount"
          label="Does an overdraft account exist?"
        >
          <template #control>
            <POptionGroup
              v-model="isOverdraftAccount"
              :options="[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]"
            />
          </template>
        </PField>
      </div>
      <div v-if="isOverdraftAccount" class="col-6">
        <PInput
          v-model.trim="overdraftAccountsCount"
          label="Overdraft Accounts"
          placeholder="0"
          :validations="{ gte: 0, lte: 5 }"
          type="number"
          required
        />
      </div>
    </div>
    <div
      v-for="(overdraft, index) in formData.overdraft"
      :key="index"
      class="row q-col-gutter-xl"
    >
      <div class="col-9">
        <q-separator color="neutral-40" class="q-mt-xs" />
      </div>
      <div class="col-3 text-sm text-neutral-40">Overdraft {{ index + 1 }}</div>
      <div class="col-6">
        <PInput
          v-model.trim="overdraft.account_name"
          :label="`Account Name ${index + 1}`"
          placeholder="Enter account name"
          required
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim="overdraft.balance"
          label="Amount"
          placeholder="0"
          type="number"
          :validations="{ gt: 0 }"
          required
          class="prepend-bg"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { ref, computed, watch, watchEffect } from "vue";
import directorFormModel from "../models/directorForm";
const pForm = ref(null);
const { directorFormState, toggleDirectorForm, putDirectorDetails } =
  useUserStore();

const isOverdraftAccount = ref(false);
const overdraftAccountsCount = ref(0);

const props = defineProps({
  director: {
    type: Object,
    default: () => ({}),
  },
  onDirectorCreditScoreUpdated: {
    type: Function,
    default: null,
  },
});

const { formData, isLoading, closeForm, submitForm } = useForm({
  formModel: directorFormModel(),
  formRef: pForm,
  isForm: directorFormState,
  post: putDirectorDetails,
  toggleForm: toggleDirectorForm,
});

const resetState = () => {
  isOverdraftAccount.value = false;
};

const closeAction = () => {
  closeForm(true);

  resetState();
};

const formHeading = computed(() => {
  return `Edit Credit Summary - ${unref(props).director?.name}`;
});

const updateButton = computed(() => ({
  label: "Update",
  loading: unref(isLoading),
}));

const onSubmit = async () => {
  await submitForm();

  await props.onDirectorCreditScoreUpdated?.();

  resetState();
};

watch(isOverdraftAccount, (value) => {
  if (!value) {
    overdraftAccountsCount.value = 0;
  }
});

watch(overdraftAccountsCount, (newCount) => {
  const currentCount = formData.value.overdraft.length;

  if (newCount > currentCount && newCount > 0 && newCount <= 5) {
    for (let i = currentCount; i < newCount; i++) {
      formData.value.overdraft.push({ account_name: "", balance: 0 });
    }
  } else if (newCount > 5) {
    if (currentCount < 5) {
      for (let i = currentCount; i < 5; i++) {
        formData.value.overdraft.push({ account_name: "", balance: 0 });
      }
    } else {
      formData.value.overdraft.splice(5);
    }
  } else {
    formData.value.overdraft.splice(newCount);
  }
});

watchEffect(() => {
  if (unref(props).director?._id) {
    unref(formData)._id = unref(props).director._id;
  }
});
</script>
